import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Monitor, Smartphone, RefreshCw, ExternalLink, Copy, Globe, Loader2, UtensilsCrossed } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageHeader, Loading } from "@/components/dash/ui";
import { Button } from "@/components/ui/button";

export default function WebsitePreview() {
  const [r, setR] = useState(null);
  const [device, setDevice] = useState("desktop");
  const [reloadKey, setReloadKey] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get("/restaurant").then((res) => setR(res.data)); }, []);

  if (!r) return <Loading />;

  const url = `${window.location.origin}/m/${r.slug}`;
  const isMobile = device === "mobile";

  const refresh = () => { setLoading(true); setReloadKey((k) => k + 1); };
  const copy = () => { navigator.clipboard.writeText(url); toast.success("Link copied"); };

  return (
    <div data-testid="website-preview-page">
      <PageHeader
        title="Website Preview"
        subtitle="A live preview of your public page. Menu changes appear here instantly."
        action={
          <div className="flex items-center gap-2">
            <div className="flex items-center bg-muted rounded-lg p-1">
              <button data-testid="preview-desktop" onClick={() => setDevice("desktop")}
                className={`h-8 w-9 rounded-md flex items-center justify-center transition-colors ${!isMobile ? "bg-card shadow-sm text-foreground" : "text-muted-foreground"}`}>
                <Monitor className="h-4 w-4" />
              </button>
              <button data-testid="preview-mobile" onClick={() => setDevice("mobile")}
                className={`h-8 w-9 rounded-md flex items-center justify-center transition-colors ${isMobile ? "bg-card shadow-sm text-foreground" : "text-muted-foreground"}`}>
                <Smartphone className="h-4 w-4" />
              </button>
            </div>
            <Button variant="outline" onClick={refresh} data-testid="preview-refresh" className="border-border hover:bg-muted">
              <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
            </Button>
          </div>
        }
      />

      <div className="bg-card border border-border rounded-xl p-3 mb-6 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-2 text-sm text-muted-foreground flex-1 min-w-0">
          <Globe className="h-4 w-4 text-primary shrink-0" />
          <span className="truncate font-mono">{url}</span>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="ghost" onClick={copy} data-testid="preview-copy" className="hover:bg-muted"><Copy className="h-4 w-4 mr-1.5" /> Copy link</Button>
          <a href={url} target="_blank" rel="noreferrer" data-testid="preview-open">
            <Button size="sm" className="bg-primary hover:bg-[#B35A42] text-primary-foreground"><ExternalLink className="h-4 w-4 mr-1.5" /> Open live site</Button>
          </a>
        </div>
      </div>

      <div className="flex items-start justify-between gap-4 mb-4">
        <p className="text-sm text-muted-foreground flex items-center gap-1.5">
          <UtensilsCrossed className="h-4 w-4" />
          Update your <Link to="/dashboard/menu" className="text-primary hover:underline">Menu</Link> or
          <Link to="/dashboard/profile" className="text-primary hover:underline">Profile</Link> and hit Refresh to see changes.
        </p>
      </div>

      <div className="bg-muted/40 border border-border rounded-2xl p-4 sm:p-8 flex justify-center">
        <div
          className={`relative bg-card rounded-xl overflow-hidden shadow-[0_20px_60px_rgba(26,26,26,0.15)] transition-all duration-300 ${isMobile ? "w-[390px] border-8 border-foreground rounded-[2.5rem]" : "w-full max-w-5xl border border-border"}`}
          style={{ height: isMobile ? 720 : 680 }}
        >
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-card z-10">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          )}
          <iframe
            key={reloadKey}
            data-testid="preview-iframe"
            title="Website preview"
            src={url}
            onLoad={() => setLoading(false)}
            className="w-full h-full"
          />
        </div>
      </div>
    </div>
  );
}
